import { AxisSide, type AxisSpec, PanelType, type StreamEntry, type TimeAxisPanelSpec } from "../ChartSpec";
import type { ResolvedStream } from "./resolve-streams";
import { stableStreamColor } from "./stable-stream-color";

/** Axis ids are the unit category. The generator and the split/merge transforms both count on that. */
function categoryAxis(unitCategory: string, side: AxisSide): AxisSpec {
    return { id: unitCategory, unitCategory, side };
}

/** Same object back when the axis already matches, so untouched entries keep their identity. */
export function withAxisId(stream: StreamEntry, axisId: string): StreamEntry {
    return stream.axisId === axisId ? stream : { ...stream, axisId };
}

export function buildTwoAxisPanel(leftStreams: StreamEntry[], rightStreams: StreamEntry[]): TimeAxisPanelSpec | null {
    if (leftStreams.length === 0 && rightStreams.length === 0) return null;

    const axes: AxisSpec[] = [];
    const streams: StreamEntry[] = [];

    if (leftStreams.length > 0) {
        const axisId = leftStreams[0].axisId;
        axes.push(categoryAxis(axisId, AxisSide.Left));
        streams.push(...leftStreams.map((stream) => withAxisId(stream, axisId)));
    }

    if (rightStreams.length > 0) {
        const axisId = rightStreams[0].axisId;
        axes.push(categoryAxis(axisId, AxisSide.Right));
        streams.push(...rightStreams.map((stream) => withAxisId(stream, axisId)));
    }

    return {
        id: `two-axis-panel-${leftStreams[0]?.axisId}-${rightStreams[0]?.axisId}`,
        type: PanelType.TimeAxis,
        axes,
        streams,
    };
}

export function buildCategoryPanel(unitCategory: string, streams: StreamEntry[]): TimeAxisPanelSpec {
    return {
        id: `category-panel-${unitCategory}`,
        type: PanelType.TimeAxis,
        axes: [categoryAxis(unitCategory, AxisSide.Left)],
        streams: streams.map((stream) => withAxisId(stream, unitCategory)),
    };
}

/** All streams on one panel, grouped by unit category with one left axis each. Exercises left y-axis selection. */
export function buildAllStreamsPanel(streams: StreamEntry[]): TimeAxisPanelSpec | null {
    if (streams.length === 0) return null;

    const unitCategories = [...new Set(streams.map((stream) => stream.axisId))];

    return {
        id: "all-streams-panel",
        type: PanelType.TimeAxis,
        axes: unitCategories.map((unitCategory) => categoryAxis(unitCategory, AxisSide.Left)),
        streams: unitCategories.flatMap((unitCategory) => streams.filter((stream) => stream.axisId === unitCategory)),
    };
}

export function toStreamEntry(stream: ResolvedStream): StreamEntry {
    return {
        streamId: stream.id,
        color: stableStreamColor(stream.id),
        name: stream.name,
        axisId: stream.unitCategory,
    };
}
